import {ArrUtil,Util} from 'yougile-util'
import {
  CompanySettings,
  CompanySettingsOrder,
  DataObjectId,
  Hub,
  HubData,
  newsCompanyId,
  rootNodeId,
  TaskListType,
  TasksList,
  TasksListId,
  TasksLists
} from 'yougile-model'
import {Backend} from '@common/backend'
import {Tracking, TrackingEvent} from '@common/tracking'
import {globals} from '@extensions/globals-base'
import {SharedBoardsClient} from './shared-boards-client'
import {Sync} from './sync'

type StarredReason =
  | 'personal'
  | 'group'
  | 'task'
  | 'my-tasks'
  | 'my-tasks-favourite'

export const DefaultTasksLists: TasksListId[] = ['current-tasks', 'done-tasks']

const DefaultCompanySettings: CompanySettings = {
  order: {},
  stars: {},
  orderChange: {}
}

export class UserSettings {
  private static getCompanySettings(
    companyId: DataObjectId,
    canUseProfileCompanyId = false
  ): Hub | undefined {
    if (SharedBoardsClient.getId()) {
      return undefined
    }
    const profile = Sync.getProfile()

    if (companyId === profile.companyId) {
      if (!canUseProfileCompanyId) {
        return undefined
      }

      const hub = profile.get(companyId) as Hub
      if (hub.data.companySettings) {
        return hub
      } else {
        hub.data.companySettings = DefaultCompanySettings
        hub.commit()
        return hub
      }
    }

    if (!companyId || companyId === newsCompanyId) {
      return undefined
    }
    const old = profile.get(companyId) as Hub
    if (old) {
      return old.data.companySettings ? old : undefined
    } else {
      const newSett = new Hub(profile.companyId, 'c=' + companyId)
      newSett.id = companyId
      newSett.data.companySettings = DefaultCompanySettings
      profile.add(newSett, rootNodeId, true, false)
      return newSett
    }
  }

  static getForCurrentCompany(): HubData['companySettings'] {
    const settingsHub = this.getCompanySettings(globals.getCompanyId())
    return Util.clone(settingsHub?.data?.companySettings)
  }

  public static getOrder(key: TasksListId): DataObjectId[] {
    const sett = UserSettings.getCompanySettings(globals.getCompanyId())
    return sett ? Util.clone(sett.data.companySettings?.order[key] || []) : []
  }

  public static getAllTasksOrder(): CompanySettingsOrder | undefined {
    const sett = UserSettings.getCompanySettings(globals.getCompanyId())
    return sett ? Util.clone(sett.data.companySettings?.order) : undefined
  }

  public static getOrderChange(key: TasksListId): number {
    const sett = UserSettings.getCompanySettings(globals.getCompanyId())
    return sett ? sett.data.companySettings?.orderChange?.[key] ?? 0 : 0
  }

  public static setOrder(key: TasksListId, order: DataObjectId[]): void {
    if (order) {
      const sett = UserSettings.getCompanySettings(globals.getCompanyId())

      const uniqOrder = Array.from(new Set(order))

      if (sett?.data.companySettings?.order) {
        sett.data.companySettings.order[key] = Util.clone(uniqOrder)
        sett.commit()
      }
    }
  }

  public static deleteTasksList(listId: TasksListId): void {
    const settings = UserSettings.getCompanySettings(globals.getCompanyId())
    if (!settings?.data.companySettings) {
      return
    }

    const tasksList = settings.data.companySettings.tasksLists?.[listId]
    if (tasksList?.tasks?.length) {
      const box = Sync.getCurrentSandbox()
      for (const taskId of tasksList.tasks) {
        const hub = box.getHub(taskId)
        if (hub) {
          box.remove(hub)
        }
      }
    }
    delete settings.data.companySettings.tasksLists?.[listId]

    if (settings.data.companySettings.tasksListsOrder) {
      settings.data.companySettings.tasksListsOrder =
        settings.data.companySettings.tasksListsOrder.filter(
          taskListId => taskListId !== listId
        )
    }

    if (settings.data.companySettings.order[listId]) {
      delete settings.data.companySettings.order[listId]
    }

    settings.commit()
  }

  public static addTasksList({
    title,
    id,
    type,
    createdByDefault
  }: {
    title: string
    id: DataObjectId
    type?: TaskListType
    createdByDefault?: boolean
  }): void {
    if (title) {
      const settings = UserSettings.getCompanySettings(globals.getCompanyId())
      if (settings?.data.companySettings) {
        const newItem = {
          title,
          type: type ?? 'custom',
          createdByDefault
        }

        if (!settings.data.companySettings.tasksLists) {
          settings.data.companySettings.tasksLists = {}
        }
        settings.data.companySettings.tasksLists[id] = newItem

        settings.data.companySettings.tasksListsOrder = [
          id,
          ...(settings.data.companySettings.tasksListsOrder || [
            'current-tasks',
            'done-tasks'
          ])
        ]

        settings.commit()
      }
    }
  }

  public static getTasksListsOrder(): TasksListId[] {
    const settings = UserSettings.getCompanySettings(globals.getCompanyId())

    const order = settings?.data.companySettings?.tasksListsOrder

    if (!order) {
      UserSettings.setTasksListOrder(DefaultTasksLists)

      return DefaultTasksLists
    }

    let result: TasksListId[] = Util.clone(order)

    if (order.some((orderItem, i) => order.indexOf(orderItem) < i)) {
      const orderSet = new Set<TasksListId>(order)
      result = Array.from(orderSet)
    }

    for (const defaultListId of DefaultTasksLists) {
      if (!result.find(resultItem => resultItem === defaultListId)) {
        result.push(defaultListId)
        UserSettings.setTasksListOrder(result)
      }
    }

    return result
  }

  public static getTasksList(listId: TasksListId): TasksList | undefined {
    const settings = UserSettings.getCompanySettings(globals.getCompanyId())

    return Util.clone(settings?.data.companySettings?.tasksLists?.[listId])
  }

  public static getAllTasksLists(): TasksLists | undefined {
    const settings = UserSettings.getCompanySettings(globals.getCompanyId())
    const lists = settings?.data.companySettings?.tasksLists

    if (!Backend.features.myTasksLists && lists) {
      const result: TasksLists = {}

      DefaultTasksLists.forEach(listId => {
        if (lists[listId]) {
          result[listId] = lists[listId]
        }
      })

      return result
    }

    return Util.clone(lists)
  }

  public static getIsDefaultPrivateListCreated(): boolean {
    const settings = UserSettings.getCompanySettings(globals.getCompanyId())

    return (
      Util.clone(settings?.data.companySettings?.isDefaultPrivateListCreated) ||
      false
    )
  }

  public static changeTasksListName(value: string, listId: TasksListId): void {
    const settings = UserSettings.getCompanySettings(globals.getCompanyId())

    if (settings?.data.companySettings?.tasksLists?.[listId]) {
      settings.data.companySettings.tasksLists[listId].title = value
      settings.commit()
    }
  }

  public static setTasksListOrder(tasksListsOrder: TasksListId[]): void {
    const settings = UserSettings.getCompanySettings(globals.getCompanyId())

    if (!settings?.data.companySettings) {
      return
    }

    settings.data.companySettings.tasksListsOrder = Array.from(
      new Set(tasksListsOrder)
    )
    settings.commit()
  }

  public static addTaskToCustomList(
    taskListId: TasksListId,
    taskIds: DataObjectId[],
    beginning?: boolean
  ): void {
    const settings = UserSettings.getCompanySettings(globals.getCompanyId())

    if (!settings?.data.companySettings?.tasksLists?.[taskListId]) {
      return
    }

    if (!settings.data.companySettings.tasksLists[taskListId].tasks) {
      settings.data.companySettings.tasksLists[taskListId].tasks = []
    }
    const currentTasks =
      settings.data.companySettings.tasksLists[taskListId].tasks || []
    const uniqueTaskIds = ArrUtil.unique(taskIds).filter(newTask => !currentTasks.includes(newTask))
    settings.data.companySettings.tasksLists[taskListId].tasks = beginning
      ? [...uniqueTaskIds, ...currentTasks]
      : [...currentTasks, ...uniqueTaskIds]

    settings.commit()
  }

  public static removeTaskFromCustomList(
    taskListId: TasksListId,
    taskId: DataObjectId
  ): void {
    const settings = UserSettings.getCompanySettings(globals.getCompanyId())
    const settingsTasks =
      settings?.data.companySettings?.tasksLists?.[taskListId].tasks

    if (!settingsTasks?.length) {
      return
    }

    if (settings?.data.companySettings?.tasksLists) {
      settings.data.companySettings.tasksLists[taskListId].tasks =
        settingsTasks.filter(listTaskId => listTaskId !== taskId)

      settings.commit()
    }
  }

  public static isStarred(task: {
    id: DataObjectId
    companyId: DataObjectId
  }): boolean {
    const sett = UserSettings.getCompanySettings(task.companyId, true)
    return sett ? !!(sett.data.companySettings?.stars || {})[task.id] : false
  }

  public static setStarred(
    task: {id: DataObjectId; companyId: DataObjectId},
    value: boolean,
    type: StarredReason
  ): void {
    const sett = UserSettings.getCompanySettings(task.companyId, true)
    if (!sett?.data.companySettings) {
      return
    }
    if (!sett.data.companySettings.stars) {
      sett.data.companySettings.stars = {}
    }
    if (value) {
      if (!sett.data.companySettings.stars[task.id]) {
        sett.data.companySettings.stars[task.id] = 1
        sett.commit()
      }
    } else {
      if (sett.data.companySettings.stars[task.id]) {
        delete sett.data.companySettings.stars[task.id]
        sett.commit()
      }
    }

    void Tracking.send(trackKeyMap[type][String(value)])
  }
}

const trackKeyMap: {
  [key in StarredReason]: {[key: string]: TrackingEvent}
} = {
  personal: {
    true: TrackingEvent.rightPanelPersonalChatStarredOn,
    false: TrackingEvent.rightPanelPersonalChatStarredOff
  },
  group: {
    true: TrackingEvent.rightPanelPersonalChatStarredOn,
    false: TrackingEvent.rightPanelPersonalChatStarredOff
  },
  task: {
    true: TrackingEvent.rightPanelTaskChatStarredOn,
    false: TrackingEvent.rightPanelTaskChatStarredOff
  },
  ['my-tasks']: {
    true: TrackingEvent.myTasksTaskChatStarredOn,
    false: TrackingEvent.myTasksTaskChatStarredOff
  },
  ['my-tasks-favourite']: {
    true: TrackingEvent.myTasksFavouriteTaskChatStarredOn,
    false: TrackingEvent.myTasksFavouriteTaskChatStarredOff
  }
}
